import { useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SignupForm from './SignupForm';
import ResponsiveAppBar from '../components/ResponsiveAppBar';
import { useNavigate } from 'react-router-dom';
import signUpImage from '../images/Ship Image.png'

// Moved bio text into an array for easier paragraph management
const bioParagraphs = [
  "M.L. Knaggs (Martin to anyone who's ever seen him chasing kids through a sports field) is a British-born, New Zealand-based author with a lifelong passion for science fiction and storytelling. Originally from the historic city of York in the UK, Martin made the leap to New Zealand over 16 years ago with his amazing wife and then five kids (now six—turns out they liked it here).",
  "He loves them all to bits and wouldn't change a thing… although, with six kids, let's just say there's at least one drama a week and the bank manager is on speed dial. More than a few friends have said he should write a book based solely on his family's antics—especially one kid (and yes, he knows who he is). It would either have readers in stitches… or have the family stop talking to him forever. Possibly both.",
  "For more than 30 years, Martin worked as a project manager across a range of industries. He's proud of the work—but now, having written his first sci-fi novel, it's clear to everyone who ever read one of his monthly project reports that he's been writing fiction for a long time.",
  "Martin's writing journey started with a story about an alien invasion, but somewhere along the way, he veered into writing a children's book about two kids with superpowers. Family, friends, and an editor in the U.S. all encouraged him to aim for something broader—and just like that, The Shard Legacy was born. (The alien invasion book is still coming—just waiting on a few more alien sightings.)",
  "Writing The Hard Awakening—the first book in the Shard Legacy—has been an exciting and challenging ride. He quickly discovered that writing a book takes a village. A good story is just the start (maybe 70% of the way there, on a good day). To take it the rest of the way, you need brilliant editors, honest beta readers, insightful designers, and people who'll give you the kind of feedback that stings… but makes it better. Even with all that support, Martin still feels a mix of excitement and nerves about releasing his first book—but he's also learned that not every story is for everyone, and that's okay.",
  "Martin's stories aim to blend high-stakes sci-fi horror and adventure with heart, humour, and characters you can genuinely root for. He writes about survival, family, identity, and what it means to be human—even when the world around you (or the galaxy) is falling apart. His style balances fast-paced storytelling with moments of reflection and wit—likely sharpened by decades of juggling kids, project deadlines, and far too much coffee.",
  "These days, with most of his kids grown up, Martin enjoys slightly less chaotic weekends (emphasis on slightly). His hobbies include playing golf—badly—and being regularly beaten by his 16-year-old son, who he's considering kicking out of the house purely out of self-preservation. He also enjoys listening to audiobooks (thousands of them, at last count), each one sparking the dream that finally led to publishing his first novel.",
  "He's currently working on the next instalment of The Shard Legacy, chasing down his sci-fi dreams one chapter (and coffee) at a time.",
  "And if the alien invasion book ever sees the light of day… well, you've been warned.",
];

const AboutSection = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const handleNavClick = (id: string): void => {
    navigate("/", { state: { scrollToId: id } });
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // Show only the first 2 paragraphs as preview
  const previewParagraphs = bioParagraphs.slice(0, 2);
  const remainingParagraphs = bioParagraphs.slice(2);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh' 
    }}>
      <ResponsiveAppBar onNavClick={handleNavClick} />
      
      {/* Main content area that grows to fill available space */}
      <Box
        id="about"
        sx={{
          flex: 1,
          py: { xs: 4, md: 8 },
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: { xs: 4, md: 6 },
              textAlign: 'center',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            About M.L. Knaggs
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 }
          }}>

            {/* Column 1: Image */}
            <Box sx={{
              flex: { xs: '1 1 100%', md: '0 0 300px' },
              maxWidth: { xs: '100%', md: '320px' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: { xs: 4, md: 0 },
            }}>
              <Box
                component="img"
                src="/images/martinknaggs.jpeg"
                alt="M.L. Knaggs"
                sx={{
                  width: 'min(75vw, 280px)',
                  height: 'auto',
                  aspectRatio: '1 / 1',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  mb: 3,
                  boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
                }}
              />
            </Box>

            {/* Column 2: Text Content wrapped in Paper */}
            <Paper
              id="bio"
              elevation={6}
              sx={{
                flex: { xs: '1 1 100%', md: '1 1 auto' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                p: { xs: 3, md: 4 },
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                boxShadow: '0px 10px 30px rgba(0,0,0,0.15)',
              }}
            >
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}>
                {/* Always visible preview paragraphs */}
                {previewParagraphs.map((paragraph, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      lineHeight: 1.75,
                      mb: 2.5,
                      textAlign: 'left',
                      color: '#000000'
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}

                {/* Expandable content */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  {remainingParagraphs.map((paragraph, index) => (
                    <Typography
                      key={index + previewParagraphs.length}
                      variant="body1"
                      sx={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        lineHeight: 1.75,
                        mb: index === remainingParagraphs.length - 1 ? 0 : 2.5,
                        textAlign: 'left',
                        color: '#000000'
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Collapse>

                {/* Expand/Collapse button */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: expanded ? 2 : 1 
                }}>
                  <IconButton
                    onClick={handleExpandClick}
                    aria-expanded={expanded}
                    aria-label="show more"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      },
                      boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Signup section - now positioned at the bottom */}
      <Box sx={{
        backgroundColor: '#363636ff',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        width: '100%',
        minHeight: { xs: 'auto', md: '400px' }
      }}>
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <img 
            src={signUpImage} 
            alt="Ship" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
        <Box sx={{
          flex: 1, 
          padding: '5%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Typography fontSize={{ xs: 16, sm: 20, md: 30 }} fontWeight={700} color="white">
            JOIN THE FIGHT
          </Typography>
          <Typography fontSize={{ xs: 16, sm: 20, md: 30 }} fontWeight={700} color="white" sx={{ mt: 1 }}>
            SIGN UP FOR UPDATES
          </Typography>
          <Typography 
            fontSize={{ xs: 14, sm: 16, md: 20 }} 
            fontWeight={400} 
            color="white"
            sx={{ marginTop: '5%', lineHeight: 1.5 }}
          >
            Get the first chapter free + exclusive launch updates.
            Be the first to know when new books drop, unlock bonus lore, and access early previews.
          </Typography>

          <SignupForm/>
        </Box>
      </Box>
    </Box>
  );
};

export default AboutSection;